import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useLazyGetReportStatusQuery } from '@/store/api/NewSearch/NewSearch.query'

const VALID_IN_PROGRESS_STATUSES = ['Scraping', 'Searching', 'IaAnalysis']

interface UseGeneratingReportGuardReturn {
  isValidating: boolean
  isAuthorized: boolean
}

export const useGeneratingReportGuard = (): UseGeneratingReportGuardReturn => {
  const navigate = useNavigate()
  const [getReportStatus] = useLazyGetReportStatusQuery()
  const [isValidating, setIsValidating] = useState(true)
  const [isAuthorized, setIsAuthorized] = useState(false)

  useEffect(() => {
    const validateAccess = async () => {
      try {
        const reportId = localStorage.getItem('reportId')
        const subjectId = localStorage.getItem('subjectId')

        if (!reportId || !subjectId) {
          console.warn(
            '[GeneratingReportGuard] No reportId or subjectId in localStorage',
          )
          navigate(AppRoutes.dashboard, { replace: true })
          setIsValidating(false)
          return
        }

        try {
          const response = await getReportStatus({ subjectId }).unwrap()

          if (!response?.info?.status) {
            console.warn(
              '[GeneratingReportGuard] No status in response',
              response,
            )
            navigate(AppRoutes.dashboard, { replace: true })
            setIsValidating(false)
            return
          }

          const isInProgress = VALID_IN_PROGRESS_STATUSES.includes(
            response.info.status,
          )

          if (!isInProgress) {
            console.warn(
              '[GeneratingReportGuard] Report is not in progress. Status:',
              response.info.status,
            )
            navigate(AppRoutes.dashboard, { replace: true })
            setIsValidating(false)
            return
          }

          setIsAuthorized(true)
          setIsValidating(false)
        } catch (backendError) {
          console.error(
            '[GeneratingReportGuard] Error fetching report status',
            backendError,
          )
          catchWithSentry(backendError)
          navigate(AppRoutes.dashboard, { replace: true })
          setIsValidating(false)
        }
      } catch (error) {
        console.error('[GeneratingReportGuard] Unexpected error', error)
        catchWithSentry(error)
        navigate(AppRoutes.dashboard, { replace: true })
        setIsValidating(false)
      }
    }

    validateAccess()
  }, [navigate, getReportStatus])

  return {
    isValidating,
    isAuthorized,
  }
}
